import React, { useState, useEffect } from 'react';
import { useSwipeable } from 'react-swipeable';

import michaelChenImage from '@images/testimonials/testimonial-michael-chen.png';
import danzelWilliamsImage from '@images/testimonials/testimonial-danzel-williams.png';
import davidRodriguezImage from '@images/testimonials/testimonial-david-rodriguez.png';
import emilyPatelImage from '@images/testimonials/testimonial-emily-patel.png';

// Custom hook for media queries
const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }

    const listener = () => setMatches(media.matches);
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, [matches, query]);

  return matches;
};

interface TestimonialCardProps {
  name: string;
  date: string;
  content: string;
  image: string;
}

const TestimonialCard = ({ name, date, content, image }: TestimonialCardProps) => (
  <article className="bg-gray-100 p-8 rounded-lg h-full flex flex-col m-5" role="article">
    <blockquote className="text-gray-700 mb-2 flex-grow text-left">
      <p>{content}</p>
    </blockquote>
    <footer className="flex items-center gap-3 mt-auto">
      <img 
        src={image}
        alt={`${name}'s photo`}
        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
      />
      <div className="text-left">
        <h3 className="font-bold">{name}</h3>
        <p className="text-sm text-gray-600">{date}</p>
      </div>
    </footer>
  </article>
);

const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const testimonials = [
    {
      name: "Michael Chen",
      date: "Got CRJMC inDecember 2024 for Security Guard Application",
      content: "The process was incredibly smooth and quick. I received my results much faster than expected. Instead of spending weeks going through the police station, I had everything I needed in minutes. CRJMC.net is a great service for anyone needing their background check done in Ontario quickly. The online record check system is very convenient.",
      image: michaelChenImage
    },
    {
      name: "Danzel Williams",
      date: "Got CRJMC in January 2025 for a new Security Guard License",
      content: "I was amazed at how straightforward the whole process was. Completed everything from my laptop during my lunch break. The service is transparent with pricing and delivered exactly what was promised. Would definitely recommend to other security professionals.",
      image: danzelWilliamsImage
    },
    {
      name: "David Rodriguez",
      date: "Got CRJMC in November 2024 for Private Investigation License",
      content: "As someone transitioning into private investigation work, I needed my background check done quickly. This service delivered exactly that - fast, reliable results that were accepted by the licensing board. The online process saved me so much time.",
      image: davidRodriguezImage
    },
    {
      name: "Emily Patel",
      date: "Got CRJMC in March 2024 for a Security Guard License renewal",
      content: "I was worried about getting my background check done in time for my new mall security position. This service made it so simple - the whole process took minutes and I had my results the same day. Highly recommend for anyone in the security industry.",
      image: emilyPatelImage
    }
  ];

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => {
      const step = isMobile ? 1 : 2;
      return prevIndex === 0 ? testimonials.length - step : prevIndex - step;
    });
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => {
      const step = isMobile ? 1 : 2;
      return prevIndex >= testimonials.length - step ? 0 : prevIndex + step;
    });
  };

  const handlers = useSwipeable({
    onSwipedLeft: handleNext,
    onSwipedRight: handlePrevious,
    swipeDuration: 500,
    preventScrollOnSwipe: true,
    trackMouse: false
  });

  return (
    <section 
      className="w-full" 
      aria-label="Customer Testimonials"
      role="region"
    >
      <div className="relative">
        <div 
          className="overflow-hidden" 
          {...handlers}
          aria-live="polite"
        >
          <div 
            className="flex transition-transform duration-500 ease-in-out"
            style={{
              transform: `translateX(-${currentIndex * (isMobile ? 100 : 50)}%)`,
            }}
            role="list"
          >
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="min-w-full md:min-w-[50%] px-4"
                role="listitem"
                aria-hidden={index < currentIndex || index >= currentIndex + (isMobile ? 1 : 2)}
              >
                <TestimonialCard 
                  name={testimonial.name}
                  date={testimonial.date}
                  content={testimonial.content}
                  image={testimonial.image}
                />
              </div>
            ))}
          </div>
        </div>
        <button 
          className="absolute left-0 top-1/2 -translate-y-1/2 bg-black bg-opacity-30 text-white p-2 rounded-r z-10"
          onClick={handlePrevious}
          aria-label="Previous testimonials"
        >
          <span aria-hidden="true">←</span>
        </button>
        <button 
          className="absolute right-0 top-1/2 -translate-y-1/2 bg-black bg-opacity-30 text-white p-2 rounded-l z-10"
          onClick={handleNext}
          aria-label="Next testimonials"
        >
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </section>
  );
};

export default TestimonialCarousel; 